// @flow

import React from 'react';
import type { Node } from 'react';
import { timeout } from 'promise-timeout';
import loadable from '@loadable/component';
import { IsomorphicRipple } from 'shared_components/common/preloader/IsomorphicRipple';
import { normalizeUri, normalizeLocalePath } from 'shared_services/riseart/utils/RouteUtils';
import { RedirectWithStatus } from 'shared_components/routers/RedirectWithStatus';
import { StaticDataProvider } from 'shared_static/data/Provider';
import { useUrlParams } from 'shared_data/providers/url/useUrlParams';

// Lazyload components
const LOADABLE_TIMEOUT = 10000;
const FallbackComponent = (
  <IsomorphicRipple isFullScreen isActive customStyles={{ backgroundColor: '#ffffff' }} />
);
const VIEWS_COMPONENTS = {
  CmsPage: loadable(() => timeout(import('shared_components/pages/cms/Page'), LOADABLE_TIMEOUT), {
    fallback: FallbackComponent,
  }),
};

/**
 * CmsRouter
 *
 * @param {Object} props
 * @returns {Node}
 */
const CmsRouter = (props: Object): Node => {
  const { translatedLocation: location } = useUrlParams();
  const { CmsPages: CmsPagesDataProvider } = StaticDataProvider;
  const pathname = normalizeUri(location.pathname);

  return (
    <CmsPagesDataProvider>
      {(CmsPagesList) => {
        const foundCmsPage =
          CmsPagesList[pathname] ||
          Object.keys(CmsPagesList).reduce((foundPage, page) => {
            const uri = normalizeLocalePath(page, CmsPagesList[page].localeCode);
            return uri === pathname ? { redirectedUri: uri, ...CmsPagesList[page] } : foundPage;
          }, null);

        // if exact match is found, check then if matched uri is normalized, and if not redirect to uri which includes locale
        if (foundCmsPage && !foundCmsPage.redirectedUri) {
          const normalizedUri = normalizeLocalePath(pathname, foundCmsPage.localeCode);

          // if normalized and actual uri are different, then redirect to normalized locale uri
          if (pathname !== normalizedUri) {
            return <RedirectWithStatus to={normalizedUri} />;
          }
        }

        // Render CMSPage component
        if (foundCmsPage) {
          const { CmsPage } = VIEWS_COMPONENTS;
          return <CmsPage id={foundCmsPage.id} />;
        }

        // Render FallbackComponent component
        const { fallbackComponent: FallbackComponent, ...restProps } = props;
        return FallbackComponent ? <FallbackComponent {...restProps} /> : null;
      }}
    </CmsPagesDataProvider>
  );
};

export default CmsRouter;
